import * as endpoints from '../constants/endpoints';
import axios from '../utils/axios/axios';
import { formatDate } from '../utils/functions';

const toReadableMessage = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        return value.map(toReadableMessage).filter(Boolean).join(' | ');
    }
    if (typeof value === 'object') {
        return (
            toReadableMessage(value.message) ||
            toReadableMessage(value.error) ||
            JSON.stringify(value)
        );
    }
    return String(value);
};

const getRequestErrorMessage = (error) => {
    const responseData = error?.response?.data;

    return (
        toReadableMessage(responseData?.message) ||
        toReadableMessage(responseData?.details) ||
        toReadableMessage(error?.message)
    );
};

const getRequestErrorCode = (error) => {
    const responseData = error?.response?.data;
    return responseData?.code || responseData?.errorCode || responseData?.error;
};

// * services for general dashboard
/**
 * It gets the stats of campaigns by user id user between two dates for dashboard general
 * @param from - The start date of the time period you want to get stats for.
 * @param to - The end date of the time period you want to get stats for.
 * @param userId - The id user .
 * @param rawData - This is the object that will be used to store the data.
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsOfCampaignsByUserId = async (from, to, rawData, userId) => {
    from = new Date(from);
    from = formatDate(from);
    to = new Date(to);
    to = formatDate(to);
    try {
        let newUrl = `${endpoints.getStats}/campaigns/user/${userId}?from=${from}&to=${to}`;
        let newResponse = await axios.get(newUrl);
        rawData.data = { ...JSON.parse(JSON.stringify(newResponse.data)) }; // NOTE: Fuck redux
        return newResponse;
    } catch (e) {
        console.error({ e });
    }
};

/**
 * It gets all the stats of campaigns by user id
 * @param userId - The id user .
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsOfCampaignsByUserIdHistory = async (userId) => {
    const from = 22010100;
    const to = 99123123;
    try {
        let newUrl = `${endpoints.getStats}/campaigns/user/${userId}?from=${from}&to=${to}`;
        let newResponse = await axios.get(newUrl);
        return newResponse;
    } catch (e) {
        console.error({ e });
    }
};

/**
 * It gets the stats of the user by hours between two dates
 * @param from - The start date of the period you want to get stats for.
 * @param to - The end date of the range.
 * @param userId - The id user .
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsByHours = async (from, to, userId) => {
    try {
        from = new Date(from);
        from = formatDate(from);
        to = new Date(to);
        to = formatDate(to);
        let url = `${endpoints.getStats}/hours/user/${userId}?from=${from}&to=${to}`;
        const response = await axios.get(url);

        return response;
    } catch (e) {
        console.error(e);
    }
};

/**
 * It gets the stats for a user between two dates
 * @param from - The start date of the range you want to get stats for.
 * @param to - The end date of the range.
 * @param userId - The id user .
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsByDay = async (from, to, userId) => {
    try {
        from = new Date(from);
        from = formatDate(from);
        to = new Date(to);
        to = formatDate(to);
        let url = `${endpoints.getStats}/days/user/${userId}?from=${from}&to=${to}`;
        const response = await axios.get(url);

        return response;
    } catch (e) {
        console.error(e);
    }
};

/**
 * It takes a start date, an end date and user id, formats them, and then makes an API call to get the stats for
 * the user's campaigns
 * @param from - The start date of the range you want to query.
 * @param to - The end date of the date range.
 * @param userId - The id user .
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsBrandsByUserId = async (from, to, userId) => {
    from = new Date(from);
    from = formatDate(from);
    to = new Date(to);
    to = formatDate(to);
    try {
        let newUrl = `${endpoints.getStats}/brands/user/${userId}?from=${from}&to=${to}`;
        let newResponse = await axios.get(newUrl);
        return newResponse;
    } catch (e) {
        console.error({ e });
    }
};

// * services for individual dashboard
/**
 * It takes a campaign ID, a start date, and an end date, and returns the stats for that campaign
 * between those dates
 * @param campaignId - The id of the campaign you want to get stats for.
 * @param from - The start date of the range you want to get stats for.
 * @param to - The end date of the range you want to get stats for.
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsByCampaign = async (campaignId, from, to) => {
    try {
        from = new Date(from);
        from = formatDate(from);
        to = new Date(to);
        to = formatDate(to);
        let url = `${endpoints.getStats}/days/campaign/${campaignId}?from=${from}&to=${to}`;
        const response = await axios.get(url);

        return response;
    } catch (e) {
        console.error(e);
    }
};

/**
 * It gets the stats of a campaign by hours user between two dates
 * @param campaignId - The id of the campaign you want to get stats for
 * @param from - The start date of the range you want to get stats for.
 * @param to - The end date of the range you want to get stats for.
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsByCampaignByHours = async (campaignId, from, to) => {
    try {
        from = new Date(from);
        from = formatDate(from);
        to = new Date(to);
        to = formatDate(to);
        let url = `${endpoints.getStats}/hours/campaign/${campaignId}?from=${from}&to=${to}`;
        const response = await axios.get(url);

        return response;
    } catch (e) {
        console.error(e);
    }
};

/**
 * It gets the stats of a campaign by screens user between two dates
 * @param campaignId - The id of the campaign you want to get stats for
 * @param from - The start date of the time period you want to get stats for.
 * @param to - The end date of the time period you want to get stats for.
 * @returns An object with the following properties:
 *      - currencies: ["ARS"]
 *      - totals: {ARS: {}}
 *      - stats : { ARS : []}
 */
const getStatsByScreens = async (campaignId, from, to) => {
    try {
        from = new Date(from);
        from = formatDate(from);
        to = new Date(to);
        to = formatDate(to);
        let url = `${endpoints.getStats}/screens/campaign/${campaignId}?from=${from}&to=${to}`;
        const response = await axios.get(url);

        return response;
    } catch (e) {
        console.error(e);
    }
};

const getCampaignPerformance = async (campaignId, withScreens = 0) => {
    try {
        const url = `${endpoints.getCampaignPerformance}${campaignId}?with-screens=${withScreens}`;
        const response = await axios.get(url);
        return response;
    } catch (e) {
        console.error(e);
    }
};

/**
 * It gets the spend pacing of an Evenly campaign at the current hour.
 * Unlike the other services it rethrows, so the caller can tell a campaign
 * that has not started (422 with a code) from an upstream failure (500).
 * @param campaignId - The id of the campaign.
 * @returns The spend-status body: { budget, currency, status: { daily, hourly, daily_projection, pace } }
 * @throws {{ status: number|null, code: string|null, message: string }}
 */
const getCampaignSpendStatus = async (campaignId) => {
    try {
        const url = `${endpoints.getCampaignSpendStatus}${campaignId}/spend-status`;
        const response = await axios.get(url);
        return response.data;
    } catch (e) {
        throw {
            status: e?.response?.status ?? null,
            code: e?.response?.data?.code ?? null,
            message: getRequestErrorMessage(e),
        };
    }
};

const toRequestError = (e) => ({
    status: e?.response?.status ?? null,
    code: e?.response?.data?.code ?? null,
    message: getRequestErrorMessage(e),
});

/**
 * Forecast confidence of a campaign per screen (admin only).
 * @param {{ campaignId, from: string, to: string, requestConfig?: object }} params
 *   `from`/`to` are ISO instants.
 * @returns The forecast-confidence summary: { totals, screens[], evaluated_until, requests_data_until, ... }
 * @throws {{ status: number|null, code: string|null, message: string }}
 */
const getCampaignForecastConfidence = async ({
    campaignId,
    from,
    to,
    requestConfig,
}) => {
    try {
        const params = new URLSearchParams({ from, to });
        const url = `${endpoints.getCampaignForecastConfidence}${campaignId}/forecast-confidence?${params.toString()}`;
        const response = await axios.get(url, requestConfig);
        return response.data;
    } catch (e) {
        throw toRequestError(e);
    }
};

/**
 * Hour-by-hour forecast confidence of one screen of a campaign (admin only).
 * @returns { screen, hours[], evaluated_until, requests_data_until, ... }
 * @throws {{ status: number|null, code: string|null, message: string }}
 */
const getCampaignForecastConfidenceScreen = async ({
    campaignId,
    screenId,
    from,
    to,
    requestConfig,
}) => {
    try {
        const params = new URLSearchParams({ from, to });
        const url = `${endpoints.getCampaignForecastConfidence}${campaignId}/forecast-confidence/screens/${encodeURIComponent(
            screenId
        )}?${params.toString()}`;
        const response = await axios.get(url, requestConfig);
        return response.data;
    } catch (e) {
        throw toRequestError(e);
    }
};

/**
 * returns status of screens by campaigns
 * @param campaignId - The id of the campaign you want to get the status of.
 */
const getCampaignScreensStatus = async (campaignId) => {
    try {
        let url = `${endpoints.campaigScreensStatus}/${campaignId}`;
        const response = await axios.get(url);
        return response.data;
    } catch (e) {
        console.error(e);
        return {
            error: true,
        };
    }
};

const getCampaignScreenAudit = async ({
    campaignId,
    screenId,
    fromUtc,
    aliases = [],
    requestConfig,
}) => {
    try {
        const params = new URLSearchParams();
        params.set('fromUtc', fromUtc);

        const cleanAliases = Array.from(
            new Set(
                aliases
                    .map((value) => String(value ?? '').trim())
                    .filter(Boolean)
            )
        );

        if (cleanAliases.length > 0) {
            params.set('aliases', cleanAliases.join(','));
        }

        const url = `${endpoints.getCampaignScreenAudit}/${campaignId}/screens/${screenId}?${params.toString()}`;
        const response = await axios.get(url, requestConfig);
        return response.data;
    } catch (e) {
        console.error(e);
        return {
            error: true,
            message: getRequestErrorMessage(e),
        };
    }
};

const getCampaignScreenCreatives = async ({
    campaignId,
    screenId,
    requestConfig,
}) => {
    try {
        const url = `${endpoints.getCampaignScreenAuditCreatives}/${campaignId}/screens/${screenId}/creatives`;
        const response = await axios.get(url, requestConfig);
        return response.data;
    } catch (e) {
        console.error(e);
        return {
            error: true,
            code: getRequestErrorCode(e),
            message: getRequestErrorMessage(e),
        };
    }
};

const generateCampaignScreenPurchase = async ({
    campaignId,
    screenId,
    requestConfig,
}) => {
    try {
        const response = await axios.post(
            endpoints.manualBudgetInjectionV2,
            {
                campaign: Number(campaignId),
                screen: screenId,
            },
            requestConfig
        );
        if (response.data?.error) {
            return {
                ...response.data,
                message:
                    toReadableMessage(response.data.message) ||
                    toReadableMessage(response.data.details),
            };
        }

        return response.data;
    } catch (e) {
        console.error(e);
        return {
            error: true,
            code: getRequestErrorCode(e),
            message: getRequestErrorMessage(e),
            details: e?.response?.data?.details,
        };
    }
};

const getCampaignsScreensStatus = async (campaigns) => {
    try {
        let url = `${endpoints.campaignsScreensStatus}?campaigns=${campaigns}`;
        const response = await axios.get(url);
        return response.data;
    } catch (e) {
        console.error(e);
        return {
            error: true,
        };
    }
};

export default {
    // for dashboard general
    getStatsOfCampaignsByUserId,
    getStatsBrandsByUserId,
    getStatsByDay,
    getStatsByHours,
    getCampaignsScreensStatus,
    getStatsOfCampaignsByUserIdHistory,

    // for dashboard individual
    getStatsByCampaign,
    getStatsByCampaignByHours,
    getStatsByScreens,
    getCampaignPerformance,
    getCampaignSpendStatus,
    getCampaignForecastConfidence,
    getCampaignForecastConfidenceScreen,
    getCampaignScreensStatus,
    getCampaignScreenAudit,
    getCampaignScreenCreatives,
    generateCampaignScreenPurchase,
};
